import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

import { getToken } from "../../utils/tokenStorage";
import { url } from "../../utils/config";
import { delayMessage } from "../../utils/delayMessage";
import { serverMessage } from "../../utils/serverMessage";

interface PostJob {
  postJobArr: Object[];
  isRemove: boolean;
  postJobLoading: boolean;
  inputs: {
    _id: string;
    position: string;
    company: string;
    jobType: string;
    jobLocation: string;
    jobDescription: string;
    jobRequirements: string;
    benefits: string;
    qualifications: string;
    interviewProcess: string;
    jobUrl: string;
    jobEmail: string;
    socialMedia: {
      whatsapp: string;
      telegram: string;
    };
  };
  pendingRequestTimer: any;
}

const initialState: PostJob = {
  postJobArr: [],
  isRemove: true, // to toggle side base
  postJobLoading: false, // to show loading state
  inputs: {
    _id: "",
    position: "",
    company: "",
    jobType: "",
    jobLocation: "",
    jobDescription: "",
    jobRequirements: "",
    benefits: "",
    qualifications: "",
    interviewProcess: "",
    jobUrl: "",
    jobEmail: "",
    socialMedia: {
      whatsapp: "",
      telegram: "",
    },
  },
  pendingRequestTimer: null,
};

// Post a job
export const postJobAction = createAsyncThunk(
  "postJob/submitData",
  async (postJobData: any, thunkAPI) => {
    // Destructure to exclude _id and use the rest operator to gather the rest of the properties
    const { _id, ...rest } = postJobData;
    try {
      const token = getToken();
      const { data } = await axios.post(`${url.baseUrl}/postJob`, rest, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    } catch (error: any) {
      console.log(error.response);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const postJobSlice = createSlice({
  name: "addJob",
  initialState,
  reducers: {
    // Collect all inputs on the add job page
    collectInput: (state, action) => {
      const { name, value } = action.payload;

      //   Check if the inputIdentifier belongs to socialMedia
      if (name in state.inputs.socialMedia) {
        state.inputs.socialMedia = {
          ...state.inputs.socialMedia,
          [name]: value,
        };
      } else {
        state.inputs = {
          ...state.inputs,
          [name]: value,
        };
      }
    },
    // Clear input fields when submit button is clicked
    clearInput: (state) => {
      state.inputs = initialState.inputs;
    },
  },

  extraReducers: (builder) => {
    // Add Job functionality
    builder
      .addCase(postJobAction.pending, (state) => {
        state.postJobLoading = true;
        // Start a timer to show toast message if request is still pending after 10 seconds
        state.pendingRequestTimer = setTimeout(delayMessage, 10000);
      })
      .addCase(postJobAction.fulfilled, (state, { payload }) => {
        state.inputs = initialState.inputs; // Clear fields
        toast.success(payload.data.message);
        state.postJobLoading = false;
        // Clear timeout
        state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      })
      .addCase(postJobAction.rejected, (state, { payload }: any) => {
        state.postJobLoading = false;
        serverMessage(payload); // Server error
        // Clear timeout
        state.pendingRequestTimer && clearTimeout(state.pendingRequestTimer);
      });
  },
});

export const { collectInput, clearInput } = postJobSlice.actions;

export default postJobSlice.reducer;
